import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { filter, switchMap, take, tap, timer } from 'rxjs';
import { TestCoverageAnalysisParams } from '../../../core/models/test-coverage';
import { RestService } from '../../../core/services/rest.service';

export type CoverageStatus = 'fully-covered' | 'partially-covered' | 'lightly-covered';

export interface TestCoverageStory {
  id: string;
  title: string;
  coverageStatus: CoverageStatus;
  testCasesGenerated: number;
  accuracy: number;
  coverage: number;
  uncoveredCriteria: string[];
  analysisSummary: string;
  warnings: string[];
}

export interface TestCoverageData {
  totalStoriesAnalyzed: number;
  avgTestCoverage: number;
  avgTestAccuracy: number;
  totalUncoveredCriteria: number;
  stories: TestCoverageStory[];
}

export interface TestCoverageConfigs {
  apiParams?: TestCoverageAnalysisParams;
  /** When provided, skips the API call and renders this data directly. */
  initialData?: TestCoverageData;
}

@Component({
  selector: 'app-test-coverage',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './test-coverage.component.html',
  styleUrl: './test-coverage.component.scss',
})
export class TestCoverageComponent implements OnInit {
  private readonly testCoverageService = inject(RestService);
  readonly activeModal = inject(NgbActiveModal);

  @Input() configs!: TestCoverageConfigs;

  isLoading = true;
  errorMessage: string | null = null;
  coverageData: TestCoverageData | null = null;
  overallError: string | null = null;
  isActive = false;

  progressPercentage = 0;
  progressMessage = '';

  expandedStories = new Set<string>();

  ngOnInit(): void {
    if (this.configs?.initialData) {
      this.coverageData = this.configs.initialData;
      this.isLoading = false;
    } else {
      this.loadCoverageData();
    }
  }

  private loadCoverageData(): void {
    const apiParams = this.configs?.apiParams;
    if (!apiParams) {
      this.errorMessage = 'No API configuration provided.';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;
    this.overallError = null;
    this.progressPercentage = 0;
    this.progressMessage = '';

    this.testCoverageService
      .startAnalysis(apiParams)
      .pipe(
        switchMap(() =>
          timer(0, 30_000).pipe(
            switchMap(() =>
              this.testCoverageService.getResults({
                project_id: apiParams.project_id,
                version_id: apiParams.version_id,
                config_id: apiParams.config_id,
              })
            ),
            tap(response => {
              const { status, current_step, total_steps, message } = response.data;
              if (status === 'IN_PROGRESS') {
                const step = current_step ?? 0;
                const total = total_steps ?? 1;
                this.progressPercentage = total > 0 ? Math.round((step / total) * 100) : 0;
                this.progressMessage = message ?? '';
              }
            }),
            filter(response => {
              const s = (response.data?.status ?? '').toString().toUpperCase();
              return s === 'COMPLETE' || s === 'COMPLETED';
            }),
            take(1)
          )
        )
      )
      .subscribe({
        next: (response) => {
          this.coverageData = this.mapResponseToDisplayData(response.data);
          this.overallError = response.data.overall_error ?? null;
          this.progressPercentage = 100;
          this.isLoading = false;
        },
        error: () => {
          this.errorMessage = 'Failed to load test coverage data. Please try again.';
          this.isLoading = false;
        },
      });
  }

  private parsePercentage(value: string): number {
    return parseFloat(value?.replace('%', '') ?? '0') || 0;
  }

  private deriveCoverageStatus(coverage: number): CoverageStatus {
    if (coverage === 100) return 'fully-covered';
    if (coverage >= 50) return 'partially-covered';
    return 'lightly-covered';
  }

  private mapResponseToDisplayData(data: any): TestCoverageData {
    const avgCoverage = this.parsePercentage(data.summary?.overall_average_coverage);
    const avgAccuracy = this.parsePercentage(data.summary?.overall_average_accuracy);

    const stories: TestCoverageStory[] = (data.results ?? []).map((r: any) => {
      const coverage = this.parsePercentage(r.coverage_percentage_recall);
      const accuracy = this.parsePercentage(r.test_accuracy_total);
      return {
        id: r.user_story_id,
        title: r.title,
        coverageStatus: this.deriveCoverageStatus(coverage),
        testCasesGenerated: r.total_test_scenarios ?? 0,
        accuracy,
        coverage,
        uncoveredCriteria: r.uncovered_acceptance_criteria ?? [],
        analysisSummary: r.analysis_summary ?? '',
        warnings: r.warnings ?? [],
      };
    });

    return {
      totalStoriesAnalyzed: data.total_user_stories_analyzed ?? stories.length,
      avgTestCoverage: avgCoverage,
      avgTestAccuracy: avgAccuracy,
      totalUncoveredCriteria: data.summary?.total_uncovered_acceptance_criteria ?? 0,
      stories,
    };
  }

  toggleStory(id: string): void {
    if (this.expandedStories.has(id)) {
      this.expandedStories.delete(id);
    } else {
      this.expandedStories.add(id);
    }
  }

  isExpanded(id: string): boolean {
    return this.expandedStories.has(id);
  }

  hasExpandableContent(story: TestCoverageStory): boolean {
    return story.uncoveredCriteria.length > 0 || !!story.analysisSummary;
  }

  formatCount(n: number): string {
    return String(n).padStart(2, '0');
  }

  getCoverageLabel(status: CoverageStatus): string {
    const labels: Record<CoverageStatus, string> = {
      'fully-covered': 'Fully Covered',
      'partially-covered': 'Partially Covered',
      'lightly-covered': 'Lightly Covered',
    };
    return labels[status];
  }

  getCoverageClass(status: CoverageStatus): string {
    const classes: Record<CoverageStatus, string> = {
      'fully-covered': 'tc-badge--fully',
      'partially-covered': 'tc-badge--partially',
      'lightly-covered': 'tc-badge--lightly',
    };
    return classes[status];
  }

  getCoverageBarClass(coverage: number): string {
    if (coverage >= 80) return 'tc-bar--high';
    if (coverage >= 50) return 'tc-bar--medium';
    return 'tc-bar--low';
  }
}
